import React, { useEffect, useRef, useState } from 'react';
import * as fileApi from '@/api/file';
import { renderAsync } from 'docx-preview';
import { Spin } from 'antd';

interface WordViewerProps {
    filePath?: string;
}

const WordViewer: React.FC<WordViewerProps> = ({ filePath }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    useEffect(() => {
        if (!filePath || !containerRef.current) return;
        setLoading(true);
        setError(undefined);

        const render = async () => {
            try {
                const data = await fileApi.getRawFileResourceInBlob(filePath);
                if (!data) throw new Error('Failed to load file');
                const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                if (containerRef.current) {
                    containerRef.current.innerHTML = '';
                    await renderAsync(blob, containerRef.current, undefined, {
                        className: 'docx-preview',
                        inWrapper: true,
                        ignoreWidth: false,
                        ignoreHeight: false,
                        ignoreFonts: false,
                        breakPages: true,
                        useBase64URL: true,
                    });
                }
            } catch {
                setError('Failed to render Word file.');
            } finally {
                setLoading(false);
            }
        };

        render();
    }, [filePath]);

    return (
        <div className='w-full h-full overflow-auto relative'>
            {loading && (
                <div className='absolute inset-0 flex items-center justify-center'>
                    <Spin />
                </div>
            )}
            {error && (
                <div className='flex items-center justify-center h-full text-red-500 dark:text-[#F48771]'>{error}</div>
            )}
            <div ref={containerRef} className='p-4' />
        </div>
    );
};

export default WordViewer;
